import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Press, haptic } from '@dorixona/ui'
import { formatMoney } from '@dorixona/utils'
import { BackButton } from '../../ui/BackButton'
import { Icon } from '../../ui/Icon'
import { Failed, Loading, failureText } from '../../ui/Load'
import { useCart } from '../cart/CartProvider'
import { useAddresses, useLoyalty, useOrders } from '../account/api'
import { useStores } from '../stores/api'
import { distanceText, hoursText } from '../stores/model'
import { useCurrentLocation } from '../../app/location'
import { useSession } from '../../app/session'
import { useMapsOn } from '../../app/settings'
import { openOutside } from '../../app/telegram'
import { env } from '../../config/env'
import {
  useCheckoutOptions,
  usePlaceOrder,
  useQuote,
  type PayMethod,
  type PayOption,
  type ShipMethod,
} from './api'
import {
  cardTail,
  addressKey,
  addressRejected,
  addressTitle,
  deliverySelectable,
  locatedAddresses,
  fulfilling,
  payGroups,
  payLabel,
  pickPay,
  preferredPay,
  pickupStore,
  reasonText,
  reviewPayMethods,
  spendAmount,
} from './model'
import { useChosenAddress } from './chosenAddress'
import { draftShip, draftStore, rememberShip, rememberStore, resetDraft } from './draft'
import { PayIcon } from './PayIcon'
import { AddCardSheet } from './AddCardSheet'
import { useCards } from './cards'
import { lastCardId, rememberCard } from './lastCard'
import { SpendField } from './SpendField'
import { useDebounced } from '../../app/useDebounced'
import { usePoints } from '../../ui/points'

/**
 * Оформление. Что доступно и сколько это стоит, решает сервер: аптеки с «есть
 * всё», доставка с причиной недоступности, потолок списания баллов и итог.
 * Экран показывает его ответ и отправляет обратно выбор покупателя.
 */
export function CheckoutScreen() {
  const navigate = useNavigate()
  const cart = useCart()
  const { isAuthenticated, customer } = useSession()
  const { t, i18n } = useTranslation(['checkout', 'common', 'stores'])
  const { formatPoints } = usePoints()

  // Сначала — доставка, а был прошлый заказ — его способ получения; вернулись со
  // страницы адреса или аптеки — к тому же способу. Рецепт или выключенная
  // служба переведут на самовывоз ниже.
  const chosen = useChosenAddress()
  // «На карте» у самовывоза — только когда карты включены в админке.
  const mapsOn = useMapsOn()
  const [ship, setShip] = useState<ShipMethod>(() => draftShip() ?? 'delivery')
  // Способ получения подставили один раз (или выбрали сами); дальше выбирает покупатель.
  const [shipPreset, setShipPreset] = useState(() => draftShip() !== null)
  const [pay, setPay] = useState<PayMethod>('pay_at_pharmacy')
  const [cardId, setCardId] = useState(0)
  const [addingCard, setAddingCard] = useState(false)
  // Аптека, которую выбрал сам покупатель; 0 — выбираем за него ближайшую.
  const [picked, setPicked] = useState(draftStore)
  const [spendOn, setSpendOn] = useState(false)
  const [spendText, setSpendText] = useState('')
  const [wanted, setWanted] = useState(0)
  // Расчёт уходит, когда покупатель перестал печатать сумму баллов.
  const spend = useDebounced(wanted, 350)
  const [failure, setFailure] = useState('')
  // Ключ идемпотентности живёт столько же, сколько экран: повтор нажатия не
  // создаёт второй заказ, а возвращает уже созданный (R-27).
  const idempotencyKey = useMemo(() => crypto.randomUUID(), [])

  const loyalty = useLoyalty(isAuthenticated)
  const addresses = useAddresses(isAuthenticated)
  // Адреса, по которым сервер не посчитал доставку. Такой адрес на экране не
  // держим: поле пустое, заказ с доставкой ждёт другого адреса.
  const [rejected, setRejected] = useState<string[]>([])
  // Сервер отказал в доставке и без адреса — выключена сама служба, адрес тут
  // ни при чём: его не сбрасываем.
  const [serviceOff, setServiceOff] = useState(false)
  // Не выбирали адрес на его странице — везём на основной сохранённый.
  const wantedAddress = chosen ?? locatedAddresses(addresses.data ?? []).at(0) ?? null
  const address =
    wantedAddress && (serviceOff || !rejected.includes(addressKey(wantedAddress)))
      ? wantedAddress
      : null
  const addressId = address ? addressKey(address) : ''
  // Где покупатель — сервер напишет, сколько до каждой аптеки, и поставит ближние вперёд.
  const here = useCurrentLocation(isAuthenticated && cart.lines.length > 0)
  const options = useCheckoutOptions(
    isAuthenticated && cart.lines.length > 0,
    address,
    here.data ?? null,
  )
  const cards = useCards(isAuthenticated && cart.lines.length > 0)
  // Прошлый заказ — первый в списке: им и подскажем, чем платить.
  const orders = useOrders(isAuthenticated && cart.lines.length > 0)
  // Способ оплаты подставили один раз; дальше выбирает покупатель.
  const [payPreset, setPayPreset] = useState(false)
  // Режима работы в ответе оформления нет — берём его из списка аптек сети.
  const stores = useStores()
  const place = usePlaceOrder()
  const storeId = pickupStore(options.data?.pickup ?? [], picked)
  // Выбрать — только из аптек, что соберут весь заказ; остальные не показываем.
  const pickupList = fulfilling(options.data?.pickup ?? [])

  // Уходим на страницы адреса и аптеки — выбор ждёт нас здесь.
  useEffect(() => {
    rememberShip(ship)
  }, [ship])
  useEffect(() => {
    rememberStore(picked)
  }, [picked])

  // Новое оформление — тем же способом получения, что прошлый заказ. Ждём
  // ответа по заказам: до него список выключен, пока не пришла корзина.
  useEffect(() => {
    if (shipPreset || (!orders.isSuccess && !orders.isError)) return
    if (orders.data?.at(0)?.ship === 'pickup') setShip('pickup')
    setShipPreset(true)
  }, [shipPreset, orders.isSuccess, orders.isError, orders.data])

  // Аптеку и способ оплаты выбираем за покупателя — те, что сервер считает
  // доступными; иначе первый экран встречает его отказом.
  useEffect(() => {
    const data = options.data
    // Прежний ответ, пока идёт новый, — про другой адрес: по нему не решаем.
    if (!data || options.isPlaceholderData) return
    if (address === null && data.delivery.reason === 'delivery_unavailable') {
      setServiceOff(true)
      // Курьеров нет совсем — один раз переводим на самовывоз, чтобы экран не
      // встречал покупателя тупиком; вернуть доставку руками он может. Адрес
      // выбирали сами — доставку не трогаем: он видит, почему заказ не уходит.
      if (!serviceOff && chosen === null) setShip('pickup')
    }
    // Курьер не едет по этому адресу — убираем адрес и спрашиваем без него:
    // служба работает — доставка ждёт другого адреса, выключена — адрес вернём.
    if (!serviceOff && addressRejected(address, data.delivery)) {
      setRejected((list) => [...list, addressId])
      return
    }
    // Рецепт в корзине — только самовывоз (R-11).
    if (!deliverySelectable(data.delivery, data.hasRx)) setShip('pickup')
    // `address` — свежий объект на каждый рендер; меняется он вместе с `addressId`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.data, options.isPlaceholderData, addressId, serviceOff])

  // Рецензенту магазина приложений — только оплата в аптеке (R-02a).
  const phone = customer?.phone
  const payMethods = reviewPayMethods(options.data?.pay ?? [], phone, env.reviewPhone)

  // Сменили способ получения — оплата в аптеке могла пропасть (R-29).
  useEffect(() => {
    if (!options.data) return
    setPay((current) => pickPay(current, payMethods, ship))
    // `payMethods` пересобирается на каждый рендер; меняется он вместе с ответом и номером.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.data, ship, phone])

  // Первый показ: тем же способом, что прошлый заказ, а нет его — сохранённой
  // картой. Ждём и способы, и карты, и заказы, иначе подставим не то.
  useEffect(() => {
    if (payPreset || !options.data || cards.isLoading || orders.isLoading) return
    const want = preferredPay(
      orders.data?.at(0)?.payMethod,
      lastCardId(),
      cards.data ?? [],
      payMethods,
    )
    if (want) {
      setPay(pickPay(want.pay, payMethods, ship))
      setCardId(want.cardId)
    }
    setPayPreset(true)
    // `payMethods` пересобирается на каждый рендер; меняется он вместе с ответом и номером.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPreset, options.data, cards.isLoading, orders.isLoading, phone])

  const quote = useQuote(
    { ship, storeId, pay, spend, address },
    isAuthenticated &&
      cart.lines.length > 0 &&
      (ship === 'delivery'
        ? address !== null && options.data?.delivery.available === true
        : storeId > 0),
  )

  // Корзина ещё едет (вход, обновление токена) — не пугаем «Корзина пуста».
  if (cart.loading) {
    return (
      <section className="screen checkout-wrap">
        <div className="mhead">
          <h1>{t('checkout:title')}</h1>
        </div>
        <Loading what={t('common:state.loading')} count={3} />
      </section>
    )
  }

  if (cart.lines.length === 0) {
    return (
      <section className="screen cart-wrap">
        <div className="card empty">
          <Icon name="i-cart" size={54} />
          <div className="t">{t('cart:empty')}</div>
          <div style={{ marginTop: 18 }}>
            <Button
              onPress={() => {
                void navigate('/catalog')
              }}
            >
              {t('common:actions.toCatalog')}
            </Button>
          </div>
        </div>
      </section>
    )
  }

  const data = options.data
  const maxSpend = quote.data?.maxSpend ?? 0
  const hours = new Map((stores.data ?? []).map((store) => [store.id, hoursText(store, t)]))
  const { saved, online, onReceipt } = payGroups(payMethods, ship)
  // Карты показываем, если способ есть или нет только самих карт; при другой
  // причине (рецепт, провайдер выключен) — одной строкой с причиной.
  const cardsUsable = saved ? saved.available || saved.reason === 'no_saved_card' : false
  // Платить можно тем, что на экране: картой — когда её выбрали, остальным — когда
  // сервер его разрешил. Оплата в аптеке при доставке сюда не попадает (R-29).
  const payable =
    pay === 'saved_card'
      ? cardId > 0
      : [...online, ...onReceipt].some((m) => m.code === pay && m.available)

  function placeOrder() {
    setFailure('')
    place.mutate(
      {
        key: idempotencyKey,
        input: {
          ship,
          storeId,
          pay,
          cardId,
          spend,
          address,
        },
      },
      {
        onSuccess: (order) => {
          haptic('success')
          resetDraft()
          // В следующий раз отметим ту же карту: в заказе её номера нет.
          if (pay === 'saved_card') rememberCard(cardId)
          // Онлайн-оплата: заказ уже создан и ждёт денег. Показываем его карточку —
          // она сама увидит оплату — а страницу шлюза открываем рядом, не уходя из
          // приложения. Остальное — к «готово».
          if (order.checkoutUrl) {
            void navigate(`/orders/${order.id}`, { replace: true })
            openOutside(order.checkoutUrl)
          } else void navigate('/checkout/success', { state: { number: order.number } })
        },
        onError: (error) => {
          haptic('error')
          setFailure(failureText(error, t))
        },
      },
    )
  }

  return (
    <section className="screen checkout-wrap">
      <div className="mhead">
        <BackButton to="/cart" />
        <h1>{t('checkout:title')}</h1>
      </div>

      {options.isPending ? <Loading what={t('common:state.loading')} count={4} /> : null}
      {options.isError ? (
        <Failed
          error={options.error}
          onRetry={() => {
            void options.refetch()
          }}
        />
      ) : null}

      {data ? (
        <div className="co-grid">
          <div className="co-main">
            <div className="sect">
              <div className="sect-head">
                <h2>{t('checkout:receive')}</h2>
              </div>
              <div className="card">
                <label className="opt">
                  <input
                    type="radio"
                    name="ship"
                    value="delivery"
                    checked={ship === 'delivery'}
                    disabled={!deliverySelectable(data.delivery, data.hasRx)}
                    onChange={() => {
                      setShip('delivery')
                      setShipPreset(true)
                    }}
                  />
                  <div>
                    <div className="t1">{t('common:ship.delivery')}</div>
                    <div className="t2">
                      {data.delivery.available
                        ? t('checkout:deliveryHint')
                        : reasonText(deliveryReason(data, address !== null), t)}
                    </div>
                  </div>
                  <div className="tag num">
                    {data.delivery.available ? formatMoney(data.delivery.fee) : '—'}
                  </div>
                </label>
                <label className="opt">
                  <input
                    type="radio"
                    name="ship"
                    value="pickup"
                    checked={ship === 'pickup'}
                    onChange={() => {
                      setShip('pickup')
                      setShipPreset(true)
                    }}
                  />
                  <div>
                    <div className="t1">{t('common:ship.pickup')}</div>
                    <div className="t2">{t('checkout:pickupHint')}</div>
                  </div>
                  <div className="tag">{t('common:money.free')}</div>
                </label>
              </div>
            </div>

            {ship === 'pickup' ? (
              <div className="sect">
                <div className="sect-head">
                  <h2>{t('checkout:pharmacy')}</h2>
                  {mapsOn && pickupList.some((store) => store.lat !== null) ? (
                    <Press
                      squish="chip"
                      className="sect-chip"
                      onPress={() => {
                        void navigate('/checkout/pharmacy')
                      }}
                    >
                      <Icon name="i-pin" size={15} />
                      {t('checkout:pharmacyPage.open')}
                    </Press>
                  ) : null}
                </div>
                <div className="card">
                  {pickupList.length === 0 ? (
                    <div className="list-empty">{t('checkout:noPharmacy')}</div>
                  ) : null}
                  {pickupList.map((store) => (
                    <label className="opt" key={store.storeId}>
                      <input
                        type="radio"
                        name="store"
                        value={store.storeId}
                        checked={storeId === store.storeId}
                        onChange={() => {
                          setPicked(store.storeId)
                        }}
                      />
                      <div>
                        <div className="t1">
                          {store.district}
                          {store.distanceM !== null ? (
                            <span className="co-dist num">
                              {distanceText(store.distanceM, t, i18n.language)}
                            </span>
                          ) : null}
                        </div>
                        <div className="t2">{store.address}</div>
                        {hours.has(store.storeId) ? (
                          <div className="t2">
                            {t('checkout:hours', { hours: hours.get(store.storeId) })}
                          </div>
                        ) : null}
                      </div>
                      <div className="tag">
                        {t(store.openNow ? 'checkout:open' : 'checkout:closed')}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div className="sect">
                <div className="sect-head">
                  <h2>{t('checkout:address')}</h2>
                </div>
                <div className="card">
                  <Press
                    squish="row"
                    className="opt addr-row"
                    onPress={() => {
                      void navigate('/checkout/address')
                    }}
                  >
                    <Icon name="i-pin" size={18} />
                    <div>
                      <div className="t1">
                        {address ? addressTitle(address) : t('checkout:addressPage.none')}
                      </div>
                      {address?.tag ? <div className="t2">{address.label}</div> : null}
                      <div className="t2">
                        {t(address ? 'checkout:addressPage.change' : 'checkout:addressPage.choose')}
                      </div>
                    </div>
                    <Icon name="i-chev" size={16} />
                  </Press>
                </div>
              </div>
            )}

            <div className="sect">
              <div className="sect-head">
                <h2>{t('checkout:payment')}</h2>
              </div>
              {saved && cardsUsable ? (
                <div className="pay-group" role="group" aria-label={t('checkout:cards.title')}>
                  <div className="pay-title">{t('checkout:cards.title')}</div>
                  <div className="card">
                    {(cards.data ?? []).map((card) => (
                      <label className="opt pay-opt" key={card.id}>
                        <input
                          type="radio"
                          name="pay"
                          value={`saved_card:${card.id}`}
                          checked={pay === 'saved_card' && cardId === card.id}
                          onChange={() => {
                            setPay('saved_card')
                            setCardId(card.id)
                          }}
                        />
                        <PayIcon code="saved_card" />
                        <div>
                          <div className="t1 num">
                            {card.vendor ? `${card.vendor} ` : ''}
                            {cardTail(card.number)}
                          </div>
                          <div className="t2">{t('checkout:cards.hold')}</div>
                        </div>
                      </label>
                    ))}
                    <Press
                      squish="row"
                      className="opt pay-opt pay-add"
                      onPress={() => {
                        setAddingCard(true)
                      }}
                    >
                      <span className="payicon add" aria-hidden="true">
                        <Icon name="i-plus2" size={18} />
                      </span>
                      <div className="t1">{t('checkout:cards.add')}</div>
                    </Press>
                  </div>
                </div>
              ) : null}
              {online.length > 0 || (saved && !cardsUsable) ? (
                <PayGroup
                  title={t('checkout:payOnline')}
                  methods={saved && !cardsUsable ? [...online, saved] : online}
                  pay={pay}
                  onPay={setPay}
                />
              ) : null}
              {onReceipt.length > 0 ? (
                <PayGroup
                  title={t('checkout:payOnReceipt')}
                  methods={onReceipt}
                  pay={pay}
                  onPay={setPay}
                />
              ) : null}
            </div>

            {maxSpend > 0 ? (
              <div className="sect">
                <div className="sect-head">
                  <h2>{t('checkout:points')}</h2>
                </div>
                <SpendField
                  on={spendOn}
                  text={spendText}
                  max={maxSpend}
                  balance={loyalty.data?.balance ?? 0}
                  maxPct={loyalty.data?.maxSpendPct ?? 0}
                  onToggle={(on) => {
                    // Включили — в поле сразу весь доступный потолок.
                    const text = on ? String(maxSpend) : ''
                    setSpendOn(on)
                    setSpendText(text)
                    setWanted(spendAmount(on, text, maxSpend))
                  }}
                  onText={(text) => {
                    setSpendText(text)
                    setWanted(spendAmount(true, text, maxSpend))
                  }}
                />
              </div>
            ) : null}
          </div>

          <div className="co-side">
            <div className="card totals">
              {quote.isPending ? <Loading what={t('common:state.loading')} shape="lines" /> : null}
              {quote.isError ? (
                <div className="rx-note">
                  <Icon name="i-info" size={18} />
                  {failureText(quote.error, t)}
                </div>
              ) : null}
              {quote.data ? (
                <>
                  <div className="tr">
                    <span>{t('checkout:goods')}</span>
                    <b className="num">{formatMoney(quote.data.goods)}</b>
                  </div>
                  <div className="tr">
                    <span>{t('common:ship.delivery')}</span>
                    <b className="num">
                      {quote.data.deliveryFee
                        ? formatMoney(quote.data.deliveryFee)
                        : t('common:money.freeLower')}
                    </b>
                  </div>
                  {quote.data.spend > 0 ? (
                    <div className="tr">
                      <span>{t('checkout:spent')}</span>
                      <b className="num" style={{ color: 'var(--brand)' }}>
                        −{formatMoney(quote.data.spend)}
                      </b>
                    </div>
                  ) : null}
                  <div className="tr">
                    <span>{t('checkout:cashback')}</span>
                    <b className="num" style={{ color: 'var(--gold)' }}>
                      +{formatPoints(quote.data.cashback)}
                    </b>
                  </div>
                  <div className="tr grand">
                    <span>{t('checkout:total')}</span>
                    <b className="num">{formatMoney(quote.data.total)}</b>
                  </div>
                </>
              ) : null}
            </div>

            {failure ? (
              <div className="rx-note" role="alert" style={{ marginTop: 12 }}>
                <Icon name="i-info" size={18} />
                {failure}
              </div>
            ) : null}

            <div style={{ marginTop: 16 }}>
              <Button
                disabled={
                  (ship === 'delivery' && address === null) ||
                  !quote.data ||
                  quote.isPlaceholderData ||
                  place.isPending ||
                  wanted !== spend ||
                  !payable
                }
                onPress={placeOrder}
              >
                {place.isPending ? t('checkout:placing') : t('checkout:place')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <AddCardSheet
        opened={addingCard}
        onClose={() => {
          setAddingCard(false)
        }}
        onAdded={(card) => {
          setPay('saved_card')
          setCardId(card.id)
        }}
      />
    </section>
  )
}

interface PayGroupProps {
  title: string
  methods: PayOption[]
  pay: PayMethod
  onPay: (code: PayMethod) => void
}

/** Одна группа способов оплаты: онлайн или при получении. */
function PayGroup({ title, methods, pay, onPay }: PayGroupProps) {
  const { t } = useTranslation('checkout')
  return (
    <div className="pay-group" role="group" aria-label={title}>
      <div className="pay-title">{title}</div>
      <div className="card">
        {methods.map((method) => (
          <label className="opt pay-opt" key={method.code}>
            <input
              type="radio"
              name="pay"
              value={method.code}
              checked={pay === method.code}
              disabled={!method.available}
              onChange={() => {
                onPay(method.code)
              }}
            />
            <PayIcon code={method.code} />
            <div>
              <div className="t1">{payLabel(method.code, t)}</div>
              {method.available ? null : <div className="t2">{reasonText(method.reason, t)}</div>}
            </div>
          </label>
        ))}
      </div>
    </div>
  )
}

/**
 * Что написать под недоступной доставкой. Рецепт — его причина, даже если
 * сервер назвал другую; без адреса — просьба его указать: способ выбирается
 * и так, а адрес нужен первым; иначе — причина сервера.
 */
function deliveryReason(
  data: { hasRx: boolean; delivery: { reason: string } },
  hasAddress: boolean,
): string {
  if (data.hasRx) return 'rx_requires_pickup'
  if (!hasAddress && data.delivery.reason !== 'rx_requires_pickup') return 'address_required'
  return data.delivery.reason
}
